import React, { useState } from "react";

import {
    Banner,
    Button,
    LabeledField,
    LanguageSegmentedControl,
    RadioGroup,
    SectionCard,
    Stack,
    Textarea,
    ToggleSwitch,
} from "shared/ui";
import { DeliveryTypeDto } from "services/menu/restModels";

import { DELIVERY_TYPES } from "features/MenuSettings/constants";
import { useMenuSettingsContext } from "features/MenuSettings/context";

import { DeliveryConditionsDialog } from "./components/DeliveryConditionsDialog";
import { DeliveryZonesDialog } from "./components/DeliveryZonesDialog";
import { ConditionsSummary, SummaryText } from "./styles";

type ConditionsKind = "FixedPrice" | "ZonePrice";

const CONDITIONS_OPTIONS: { value: ConditionsKind; label: string; description: string }[] = [
    {
        value: "FixedPrice",
        label: "Фиксированная стоимость",
        description: "Стоимость доставки одинаковая вне зависимости от адреса.",
    },
    {
        value: "ZonePrice",
        label: "Стоимость по зонам",
        description: "Подходит, если стоимость доставки отличается в зависимости от адреса.",
    },
];

export const OrderMethodsTab = () => {
    const {
        delivery,
        menuFlags,
        locale,
        currencyId,
        currencyFormatter,
        hasExternalIntegration,
        ensureTariff,
    } = useMenuSettingsContext();

    const [activeLang, setActiveLang] = useState<string>(locale);
    const [isConditionsOpen, setIsConditionsOpen] = useState<boolean>(false);
    const [isZonesOpen, setIsZonesOpen] = useState<boolean>(false);

    // Доставку до машины при интеграции с iiko не показываем — она туда не уезжает.
    const visibleTypes = DELIVERY_TYPES.filter(
        (item) => item.type !== "ToVehicle" || !hasExternalIntegration
    );

    const conditions = delivery.toAddress.deliveryConditions;

    const renderExtraControls = (type: DeliveryTypeDto) => {
        if (type === "ToRestaurantTable" && delivery.toRestaurantTable.isEnabled) {
            return (
                <ToggleSwitch
                    checked={!delivery.toRestaurantTable.isChangeTableEnabled}
                    label="Запретить гостю изменять номер стола"
                    onChange={(checked) => delivery.setIsChangeTableEnabled(!checked)}
                />
            );
        }

        if (type === "TakeAwayToTime" && delivery.takeAwayToTime.isEnabled) {
            return (
                <ToggleSwitch
                    checked={delivery.takeAwayToTime.isDateRequired}
                    label="С выбором даты"
                    onChange={delivery.setIsDateRequired}
                />
            );
        }

        if (type !== "ToAddress" || !delivery.toAddress.isEnabled) return null;

        return (
            <>
                <RadioGroup
                    value={conditions.type}
                    options={CONDITIONS_OPTIONS}
                    onChange={(value) => {
                        if (value === conditions.type) return;

                        delivery.setDeliveryConditions(
                            value === "ZonePrice"
                                ? { type: "ZonePrice", deliveryZones: [] }
                                : {
                                      type: "FixedPrice",
                                      fixedDeliveryPrice: { currencyId, price: 0 },
                                      freeDeliveryMinimalOrderPrice: { currencyId, price: 0 },
                                  }
                        );
                    }}
                />

                <ConditionsSummary>
                    {conditions.type === "FixedPrice" ? (
                        <SummaryText>
                            Доставка{" "}
                            {currencyFormatter.toShow(conditions.fixedDeliveryPrice.price)}
                            {conditions.freeDeliveryMinimalOrderPrice.price > 0 &&
                                `, бесплатно от ${currencyFormatter.toShow(
                                    conditions.freeDeliveryMinimalOrderPrice.price
                                )}`}
                        </SummaryText>
                    ) : (
                        <SummaryText>
                            {conditions.deliveryZones.length > 0
                                ? `Настроено зон: ${conditions.deliveryZones.length}`
                                : "Зоны ещё не загружены"}
                        </SummaryText>
                    )}
                    <Button
                        variant="outlined"
                        onClick={() =>
                            conditions.type === "ZonePrice"
                                ? setIsZonesOpen(true)
                                : setIsConditionsOpen(true)
                        }
                    >
                        Настроить
                    </Button>
                </ConditionsSummary>
            </>
        );
    };

    return (
        <>
            {hasExternalIntegration && (
                <Banner tone="info">
                    Способы заказа синхронизированы с iiko — часть настроек задаётся там.
                </Banner>
            )}

            {visibleTypes.map((item) => {
                const settings = delivery.deliveryByType[item.type];

                return (
                    <SectionCard
                        key={item.type}
                        title={item.title}
                        description={item.description}
                        headerRight={
                            <LanguageSegmentedControl
                                languages={menuFlags.enabledLocales}
                                activeLang={activeLang}
                                primaryLang={locale}
                                onChange={setActiveLang}
                                isMissing={(lang) => !settings.description[lang]}
                            />
                        }
                    >
                        <Stack>
                            <ToggleSwitch
                                checked={settings.isEnabled}
                                label="Включить способ заказа"
                                onChange={(checked) => {
                                    if (!ensureTariff("Basic")) return;
                                    delivery.setEnabled(item.type, checked);
                                }}
                            />

                            <LabeledField
                                label="Описание"
                                hint="Гость увидит его при выборе способа заказа"
                            >
                                <Textarea
                                    value={settings.description[activeLang]}
                                    rows={2}
                                    onChange={(value) =>
                                        delivery.setLocalizedDescription(
                                            item.type,
                                            value,
                                            activeLang
                                        )
                                    }
                                />
                            </LabeledField>

                            {renderExtraControls(item.type)}
                        </Stack>
                    </SectionCard>
                );
            })}

            <DeliveryConditionsDialog
                open={isConditionsOpen}
                onClose={() => setIsConditionsOpen(false)}
            />
            <DeliveryZonesDialog open={isZonesOpen} onClose={() => setIsZonesOpen(false)} />
        </>
    );
};
